import { useEffect, useState } from 'react';
import { AlertCircle, Check } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ADMISSION_SECTIONS, sectionAnchorId } from '../sections';

export interface SectionProgress {
  key: string;
  /** How many of the section's compulsory fields are filled. */
  done: number;
  /** How many it has. Zero means nothing here is compulsory. */
  total: number;
  /** Fields in this section the resolver has rejected. */
  errors: number;
}

export interface AdmissionProgressRailProps {
  sections: SectionProgress[];
  /** Every compulsory field, and how many of them are filled. */
  overall: { done: number; total: number };
}

/**
 * How far through the admission the office is.
 *
 * A spine rather than a strip of tabs: this form is one page and stays one page
 * — an admission is taken with the family across the desk and the office fills
 * in whatever is to hand first, so nothing here may gate anything else. The
 * rail only *reports*. It says what is done, what still needs checking, and
 * takes you to the panel; it never hides one.
 *
 * The line down the left fills as the compulsory fields do, so the whole form's
 * state is one glance rather than a scroll.
 */
export function AdmissionProgressRail({ sections, overall }: AdmissionProgressRailProps) {
  const percent = overall.total > 0 ? Math.round((overall.done / overall.total) * 100) : 0;
  const badSections = sections.filter((section) => section.errors > 0).length;
  const [activeKey, setActiveKey] = useState<string>(ADMISSION_SECTIONS[0].key);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const key = entry.target.id.replace('admission-section-', '');
            if (key) setActiveKey(key);
          }
        });
      },
      { rootMargin: '-10% 0px -50% 0px' },
    );

    ADMISSION_SECTIONS.forEach((spec) => {
      const el = document.getElementById(sectionAnchorId(spec.key));
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-3 sm:p-3.5 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)]">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[11px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">
          Admission progress
        </p>
        <p className="shrink-0 text-[13px] font-black tabular-nums text-slate-800 dark:text-slate-100">
          {percent}%
        </p>
      </div>

      {/* A runtime width can only be an inline style — `w-[${n}%]` is a class
          the compiler never sees. Same reason `components/ui/ProgressBar` gives. */}
      <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <span
          className="block h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500"
          style={{ width: `${percent}%` }}
        />
      </span>

      <p className="mt-1.5 text-[11px] font-bold text-slate-500 dark:text-slate-400">
        {overall.done} of {overall.total} required details
        {badSections > 0 && (
          <span className="text-rose-600 dark:text-rose-400">
            {' '}
            · {badSections} {badSections === 1 ? 'section needs' : 'sections need'} checking
          </span>
        )}
      </p>

      <ol className="relative mt-2.5 flex flex-col gap-1.5">
        {/* The spine the nodes sit on. Behind them, and decoration only. */}
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-4 left-[19px] top-4 w-0.5 bg-slate-300 dark:bg-slate-600/80"
        />

        {ADMISSION_SECTIONS.map((spec, index) => {
          const progress = sections.find((section) => section.key === spec.key);
          const done = progress?.done ?? 0;
          const total = progress?.total ?? 0;
          const errors = progress?.errors ?? 0;

          const isComplete = errors === 0 && total > 0 && done === total;
          const isStarted = done > 0;
          const isActive = activeKey === spec.key;
          const Icon = spec.icon;

          return (
            <li key={spec.key} className="relative">
              <a
                href={`#${sectionAnchorId(spec.key)}`}
                onClick={() => setActiveKey(spec.key)}
                style={{ animationDelay: `${index * 60}ms` }}
                className={cn(
                  'group flex animate-card-pop items-center gap-2.5 rounded-xl border p-1.5 transition-all',
                  isActive
                    ? 'border-blue-200 dark:border-blue-800/70 bg-blue-50/60 dark:bg-blue-950/30 shadow-xs'
                    : 'border-transparent hover:border-blue-200 dark:hover:border-blue-800/70 hover:bg-blue-50/60 dark:hover:bg-blue-950/30',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50',
                )}
              >
                <span
                  className={cn(
                    'relative z-10 grid h-7 w-7 shrink-0 place-items-center rounded-full border-2 transition-all',
                    errors > 0
                      ? 'border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400'
                      : isComplete
                        ? 'border-blue-500 bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md shadow-blue-500/25'
                        : isStarted
                          ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400'
                          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-[#111c2e] text-slate-400 dark:text-slate-500',
                  )}
                  aria-hidden
                >
                  {errors > 0 ? (
                    <AlertCircle className="h-3.5 w-3.5" />
                  ) : isComplete ? (
                    <Check className="h-3.5 w-3.5 stroke-[3]" />
                  ) : (
                    <Icon className="h-3.5 w-3.5" />
                  )}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-1.5">
                    <span
                      className={cn(
                        'truncate text-[13px] font-extrabold',
                        errors > 0
                          ? 'text-rose-700 dark:text-rose-300'
                          : isComplete
                            ? 'text-blue-700 dark:text-blue-300'
                            : 'text-slate-700 dark:text-slate-200',
                      )}
                    >
                      {spec.label}
                    </span>
                    {total > 0 && (
                      <span className="ml-auto shrink-0 font-mono text-[10px] font-bold tabular-nums text-slate-400 dark:text-slate-600">
                        {done}/{total}
                      </span>
                    )}
                  </span>
                  <span
                    className={cn(
                      'mt-0.5 block truncate text-[11px] font-medium',
                      errors > 0
                        ? 'text-rose-600 dark:text-rose-400'
                        : 'text-slate-400 dark:text-slate-500',
                    )}
                  >
                    {errors > 0
                      ? `${errors} ${errors === 1 ? 'field needs' : 'fields need'} checking`
                      : spec.hint}
                  </span>
                </span>
              </a>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
